/**
 * TS wrapper over the xiangqi-core WASM module (hand-rolled ABI, no bindgen).
 *
 * The same wrapper runs in Node (server) and the browser (client):
 * pass raw wasm bytes to {@link instantiateCore}. Node callers can use the
 * convenience {@link loadCoreFromFile} which reads the artifact shipped in
 * this package (`wasm/xiangqi_core.wasm`, produced by scripts/build-wasm.sh).
 */
export class CoreError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'CoreError';
    }
}
const encoder = new TextEncoder();
const decoder = new TextDecoder();
export async function instantiateCore(wasmBytes) {
    const { instance } = await WebAssembly.instantiate(wasmBytes, {});
    const exports = instance.exports;
    for (const name of ['memory', 'xq_alloc', 'xq_free', 'xq_call']) {
        if (!(name in exports)) {
            throw new CoreError('BAD_WASM', `wasm module is missing export '${name}'`);
        }
    }
    function call(op, payload) {
        const request = encoder.encode(JSON.stringify(payload === undefined ? { op } : { op, payload }));
        const reqPtr = exports.xq_alloc(request.length);
        new Uint8Array(exports.memory.buffer, reqPtr, request.length).set(request);
        let packed;
        try {
            packed = exports.xq_call(reqPtr, request.length);
        }
        finally {
            exports.xq_free(reqPtr, request.length);
        }
        const outPtr = Number(packed >> 32n);
        const outLen = Number(packed & 0xffffffffn);
        // Copy before freeing — the view becomes invalid once the buffer is freed
        // (and on any later memory.grow).
        const outBytes = new Uint8Array(exports.memory.buffer, outPtr, outLen).slice();
        exports.xq_free(outPtr, outLen);
        const response = JSON.parse(decoder.decode(outBytes));
        if (!response.ok) {
            const e = response.error ?? { code: 'UNKNOWN', message: 'engine returned ok:false without error' };
            throw new CoreError(e.code, e.message);
        }
        return response.data;
    }
    return { call };
}
/** Node-only convenience: load the wasm artifact bundled with this package. */
export async function loadCoreFromFile() {
    const { readFile } = await import('node:fs/promises');
    const wasmUrl = new URL('../wasm/xiangqi_core.wasm', import.meta.url);
    const bytes = await readFile(wasmUrl);
    return instantiateCore(bytes);
}
export function coreVersion(core) {
    return core.call('version');
}
/** Ergonomic wrapper: one rules engine, typed ops. */
export class XiangqiCore {
    core;
    constructor(core) {
        this.core = core;
    }
    /** Escape hatch cho op mới (vd `search`) chưa có method riêng. */
    call(op, payload) {
        return this.core.call(op, payload);
    }
    newGame(ruleset, seed, handicap) {
        const payload = { ruleset };
        if (seed !== undefined)
            payload.seed = typeof seed === 'bigint' ? seed.toString() : seed;
        if (handicap !== undefined)
            payload.handicap = handicap;
        return this.core.call('new_game', payload).state;
    }
    legalMoves(state) {
        return this.core.call('legal_moves', { state });
    }
    applyMove(state, move) {
        return this.core.call('apply_move', { state, move });
    }
    applyDelta(state, move, reveal, captured, reveals) {
        const payload = { state, move };
        if (reveal !== undefined)
            payload.reveal = reveal;
        if (captured !== undefined)
            payload.captured = captured;
        if (reveals !== undefined)
            payload.reveals = reveals;
        return this.core.call('apply_delta', payload).state;
    }
    publicView(state) {
        return this.core.call('public_view', { state }).state;
    }
    publicViewFor(state, viewer) {
        return this.core.call('public_view_for', { state, viewer: viewer ?? null }).state;
    }
    stateHash(state) {
        return this.core.call('state_hash', { state }).hash;
    }
    search(state, limits) {
        return this.core.call('search', { state, ...limits });
    }
}
/** Node-only convenience: instantiate the bundled wasm and wrap it. */
export async function loadXiangqiCore() {
    return new XiangqiCore(await loadCoreFromFile());
}
/** Browser convenience: fetch the wasm (e.g. a Vite `?url` import) and wrap it. */
export async function loadXiangqiCoreFromUrl(url) {
    const res = await fetch(url);
    if (!res.ok)
        throw new CoreError('BAD_WASM', `failed to fetch wasm: HTTP ${res.status}`);
    return new XiangqiCore(await instantiateCore(await res.arrayBuffer()));
}
//# sourceMappingURL=index.js.map